import React, { PropsWithChildren } from 'react';
import { useDispatch } from 'react-redux';
import { ErrorBoundary as SentryErrorBoundary } from '@sentry/react';
import { Box, Button, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import { previousModalPage } from 'actions/ModalPage/previousModalPage';
import * as ErrorFallback from 'components/ErrorFallback';

interface Props {
  component: string;
  props: Record<string, any>;
}

function ErrorBoundary({
  component,
  props,
  children,
}: PropsWithChildren<Props>) {
  const dispatch = useDispatch();
  const { t } = useTranslation(['Sentry', 'modalpage']);
  const componentName = t('modalpageName', {
    ns: 'modalpage',
    context: component,
    defaultValue: component,
  });

  return (
    <SentryErrorBoundary
      beforeCapture={scope => {
        scope.setTag('component', 'modal');
        scope.setTag('subcomponent', component);
      }}
      fallback={({ resetError, eventId }) => (
        <ErrorFallback.Container>
          <ErrorFallback.Title
            title={t('modalErrorBoundary.title', { ns: 'Sentry' })}
          />
          <Box padding="0 1rem">
            <Typography paragraph>
              {t('modalErrorBoundary.subtitle', {
                ns: 'Sentry',
                modalName: componentName,
              })}
            </Typography>
          </Box>
          <ErrorFallback.Actions
            resetError={resetError}
            eventId={eventId}
            showReloadPageButton={false}
            extraButtons={
              <Button
                variant="outlined"
                disableElevation
                onClick={() => {
                  // Make sure that the promise that openModalPage returns is settled
                  props.reject();
                  dispatch(previousModalPage());
                }}
              >
                {t('modalErrorBoundary.buttons.close', { ns: 'Sentry' })}
              </Button>
            }
          />
        </ErrorFallback.Container>
      )}
    >
      {children}
    </SentryErrorBoundary>
  );
}

export default ErrorBoundary;
